/**
 * External identity-provider federation — query + mutation hooks.
 * Tenant-scoped against the canonical inbound-federation system:
 *   /api/v1/tenants/{tenant_id}/federation[/{provider_id}].
 * Mutations are step-up-gated on the backend (federation.manage), so wrapped in
 * withStepUp. The client_secret is write-only — sent on create/rotate, never read
 * back (the list/responses expose no secret material).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { withStepUp } from "@/lib/with-step-up";
import { queryKeys } from "@/lib/query-keys";
import {
  federationProviderSchema,
  federationProvidersResponseSchema,
  providerTestGrantSchema,
  type CreateFederationProviderInput,
  type FederationProvider,
  type FederationProvidersResponse,
} from "./schemas";

export const federationKeys = {
  all: ["federation-providers"] as const,
  list: (tenantID: string) => ["federation-providers", tenantID] as const,
  organization: (organizationID: string) => ["federation-providers", "organization", organizationID] as const,
};

export type FederationAPIScope = "tenant" | "organization";

export function federationBase(tenantID: string, scope: FederationAPIScope = "tenant"): string {
  return scope === "organization"
    ? "/api/org/v1/federation"
    : `/api/v1/tenants/${encodeURIComponent(tenantID)}/federation`;
}

export async function issueProviderTest(tenantID: string, providerID: string, scope: FederationAPIScope = "tenant") {
  return withStepUp(() =>
    apiFetch<unknown>(
      `${federationBase(tenantID, scope)}/${encodeURIComponent(providerID)}/test-authorizations`,
      { method: "POST" },
    ).then((raw) => providerTestGrantSchema.parse(raw)),
  );
}

export async function fetchFederationProviders(tenantID: string): Promise<FederationProvidersResponse> {
  const raw = await apiFetch<unknown>(federationBase(tenantID));
  return federationProvidersResponseSchema.parse(raw);
}

export async function fetchOrganizationFederationProviders(): Promise<FederationProvidersResponse> {
  const raw = await apiFetch<unknown>(federationBase("", "organization"));
  return federationProvidersResponseSchema.parse(raw);
}

export function useFederationProvidersQuery(tenantID: string) {
  return useQuery<FederationProvidersResponse, ApiError>({
    queryKey: federationKeys.list(tenantID),
    queryFn: () => fetchFederationProviders(tenantID),
	// A provider test returns from a separate tab, including within staleTime.
	refetchOnWindowFocus: "always",
  });
}

export function useOrganizationFederationProvidersQuery(organizationID: string) {
  return useQuery<FederationProvidersResponse, ApiError>({
    queryKey: federationKeys.organization(organizationID),
    queryFn: fetchOrganizationFederationProviders,
	refetchOnWindowFocus: "always",
  });
}

// The create/update wire body. The backend leaves the client_secret unchanged when
// it's omitted on update, so a status toggle (below) can round-trip the row without
// the plaintext secret.
export function createProviderBody(input: CreateFederationProviderInput): Record<string, unknown> {
  return {
    ...(input.organization_id ? { organization_id: input.organization_id } : {}),
    provider_type: input.provider_type,
    display_name: input.display_name,
    client_id: input.client_id,
    ...(input.client_secret ? { client_secret: input.client_secret } : {}),
    ...(input.discovery_url ? { discovery_url: input.discovery_url } : {}),
    ...(input.scopes && input.scopes.length > 0 ? { scopes: input.scopes } : {}),
    enabled: input.enabled,
    require_email_verified: input.require_email_verified,
    trust_policy: input.trust_policy ?? null,
    allow_application_access: input.allow_application_access ?? false,
    // Omitted unlinks: an update body is the whole provider.
    ...(input.source_id ? { source_id: input.source_id } : {}),
  };
}

export function useCreateFederationProviderMutation(tenantID: string, scope: FederationAPIScope = "tenant") {
  const qc = useQueryClient();
  return useMutation<FederationProvider, ApiError, CreateFederationProviderInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(federationBase(tenantID, scope), {
          method: "POST",
          body: createProviderBody(input),
        }).then((raw) => federationProviderSchema.parse(raw)),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: federationKeys.all });
      // A provider is the sign-in half of a source: the Sources rows follow it.
      qc.invalidateQueries({ queryKey: queryKeys.identitySources.all(tenantID) });
    },
  });
}

export interface UpdateFederationProviderInput {
  expectedVersion: number;
  id: string;
  input: CreateFederationProviderInput;
}

export function useUpdateFederationProviderMutation(tenantID: string, scope: FederationAPIScope = "tenant") {
  const qc = useQueryClient();
  return useMutation<FederationProvider, ApiError, UpdateFederationProviderInput>({
    mutationFn: ({ id, input, expectedVersion }) =>
      withStepUp(() =>
        apiFetch<unknown>(`${federationBase(tenantID, scope)}/${encodeURIComponent(id)}`, {
          method: "PUT",
          body: { ...createProviderBody(input), expected_version: expectedVersion },
        }).then((raw) => federationProviderSchema.parse(raw)),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: federationKeys.all });
      // A provider is the sign-in half of a source: the Sources rows follow it.
      qc.invalidateQueries({ queryKey: queryKeys.identitySources.all(tenantID) });
    },
  });
}

// Enable/disable toggles `enabled` via a full PUT. The backend preserves the
// client_secret on an empty value, so we round-trip the row's non-secret fields
// with `enabled` flipped — no secret needed (and none is available client-side).
export interface SetFederationEnabledInput {
 activateRevision?: number;
  provider: FederationProvider;
  enabled: boolean;
}

export function useSetFederationEnabledMutation(tenantID: string, scope: FederationAPIScope = "tenant") {
  const qc = useQueryClient();
  return useMutation<FederationProvider, ApiError, SetFederationEnabledInput>({
    mutationFn: ({ provider, enabled, activateRevision }) =>
      withStepUp(() =>
        apiFetch<unknown>(`${federationBase(tenantID, scope)}/${encodeURIComponent(provider.id)}`, {
          method: "PUT",
          body: {
            expected_version: provider.version,
 ...(enabled ? { activate_revision: activateRevision ?? provider.configuration_revision } : {}),
 provider_type: provider.provider_type,
            display_name: provider.display_name,
            client_id: provider.client_id,
            ...(provider.discovery_url ? { discovery_url: provider.discovery_url } : {}),
            ...(provider.scopes.length > 0 ? { scopes: provider.scopes } : {}),
            enabled,
            require_email_verified: provider.require_email_verified,
            trust_policy: provider.trust_policy ?? null,
            allow_application_access: provider.allow_application_access ?? false,
            // A body without it would unlink the provider from its source.
            ...(provider.source_id ? { source_id: provider.source_id } : {}),
          },
        }).then((raw) => federationProviderSchema.parse(raw)),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: federationKeys.all });
      // A provider is the sign-in half of a source: the Sources rows follow it.
      qc.invalidateQueries({ queryKey: queryKeys.identitySources.all(tenantID) });
    },
  });
}

export function useDeleteFederationProviderMutation(tenantID: string, scope: FederationAPIScope = "tenant") {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (id) =>
      withStepUp(() =>
        apiFetch<void>(`${federationBase(tenantID, scope)}/${encodeURIComponent(id)}`, { method: "DELETE" }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: federationKeys.all });
      // A provider is the sign-in half of a source: the Sources rows follow it.
      qc.invalidateQueries({ queryKey: queryKeys.identitySources.all(tenantID) });
    },
  });
}
